import { useQuery } from 'react-query'

import { toast } from '@sales-center/components'

import { ServiceConfig } from '@user/store/services/config'
import { get } from '@user/store/services/crud'
import type { ILeadScheduleAutoClosable } from '@user/store/lead/typings'

const baseUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.lead}`

export const MISSING_AUTO_CLOSE_REQUIRED_FIELDS_QUERY_KEY =
  'missingAutoCloseRequiredFields'

export const SCHEDULE_LEAD_IS_CLOSABLE_QUERY_KEY = 'scheduleLeadIsClosable'

const fetchMissingAutoCloseRequiredFields = async (
  leadId: number,
): Promise<number[] | null> => {
  const response = await get(
    `${baseUrl}/${leadId}${ServiceConfig.lead.missingAutoCloseRequiredFields}`,
  )
  if (response.ok) return await response.json()
  return null
}

const fetchScheduleServiceAutoClosable = async (
  leadId: number,
): Promise<ILeadScheduleAutoClosable> => {
  const response = await get(
    `${baseUrl}${ServiceConfig.lead.isCloseable}?id=${leadId}`,
  )

  if (!response.ok) {
    throw new Error(
      `Failed to check lead auto-close eligibility (${response.status})`,
    )
  }

  return await response.json()
}

export const useMissingAutoCloseRequiredFields = (
  leadId: number,
  enabled: boolean,
) =>
  useQuery<number[] | null>(
    [MISSING_AUTO_CLOSE_REQUIRED_FIELDS_QUERY_KEY, leadId],
    () => fetchMissingAutoCloseRequiredFields(leadId),
    { enabled: enabled && !!leadId, staleTime: 0, refetchOnMount: 'always' },
  )

export const useScheduleServiceAutoClosable = (
  leadId: number,
  enabled = true,
) =>
  useQuery<ILeadScheduleAutoClosable>(
    [SCHEDULE_LEAD_IS_CLOSABLE_QUERY_KEY, leadId],
    () => fetchScheduleServiceAutoClosable(leadId),
    {
      enabled: enabled && !!leadId,
      staleTime: 0,
      refetchOnMount: 'always',
      retry: false,
      onError: () =>
        toast.error(
          'Unable to check whether this lead will auto-close upon scheduling.',
        ),
    },
  )
